import { CaretDown } from '@app/components/icons/caret-down';
import { Listbox, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import type { TFunction } from 'i18next';
import React, { Fragment } from 'react';

import { ThemeCard } from '../../../../../../components/color-presets/theme-card';
import type { BrandingPreset } from '../../../../../../domain';
import { ColorLabels } from '../../../../../../theme/constants';

type EditPresetSelectorProps = {
  presets: BrandingPreset[];
  selectedPresetId: string | undefined;
  onPresetSelect: (preset: BrandingPreset) => void;
  disabled?: boolean;
};

const getPresetName = (t: TFunction, preset: BrandingPreset) =>
  preset.isPredefined
    ? t(`content-recording.appearance.color-presets.names.${preset.id}`)
    : preset.name;

export const EditPresetSelector = ({
  presets,
  selectedPresetId,
  onPresetSelect,
  disabled = false,
}: EditPresetSelectorProps) => {
  const { t } = useTranslation();
  const currentPreset =
    presets.find(p => p.id === selectedPresetId) ?? presets[0];

  if (!currentPreset || presets.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full">
      <Listbox
        value={currentPreset.id}
        onChange={(presetId: string) => {
          const preset = presets.find(p => p.id === presetId);
          if (preset) onPresetSelect(preset);
        }}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <Listbox.Button
              className={cx(
                'flex w-full min-h-[68px] items-center justify-between rounded-[10px] border border-solid border-spring-neutral-b0/20 text-white hover:bg-spring-neutral-b0/10',
                open && 'bg-spring-neutral-b0/10',
              )}
            >
              <div className="flex flex-1 items-center gap-2 px-4 py-1">
                <ThemeCard
                  name={getPresetName(t, currentPreset)}
                  isDefault={currentPreset.isDefault}
                  primary={
                    currentPreset.config[ColorLabels['BRAND-PRIMARY']] as string
                  }
                  background={
                    currentPreset.config[
                      ColorLabels['NEUTRAL-PRIMARY']
                    ] as string
                  }
                  text={
                    currentPreset.config[
                      ColorLabels['DARK-SECONDARY']
                    ] as string
                  }
                />
              </div>
              <CaretDown
                className={cx('mr-3 size-5 shrink-0', open && 'rotate-180')}
              />
            </Listbox.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Listbox.Options className="absolute left-0 z-50 mt-2 max-h-96 w-full overflow-y-auto rounded-lg border border-spring-neutral-b0/20 bg-spring-neutral-base shadow-lg">
                <div className="p-2">
                  {presets.map(preset => (
                    <Listbox.Option
                      key={preset.id}
                      value={preset.id}
                      className={({ active }) =>
                        cx(
                          'cursor-pointer rounded-md px-3 py-2.5',
                          active && 'bg-spring-neutral-b0/10',
                        )
                      }
                    >
                      <ThemeCard
                        name={getPresetName(t, preset)}
                        isDefault={preset.isDefault}
                        primary={
                          preset.config[ColorLabels['BRAND-PRIMARY']] as string
                        }
                        background={
                          preset.config[
                            ColorLabels['NEUTRAL-PRIMARY']
                          ] as string
                        }
                        text={
                          preset.config[ColorLabels['DARK-SECONDARY']] as string
                        }
                      />
                    </Listbox.Option>
                  ))}
                </div>
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
};
